/* eslint-disable no-console */
import {
  Form,
  Input,
  Row,
  Col,
  Radio,
  DatePicker,
  Drawer,
  Button,
  Divider,
  Space,
} from 'antd';
import locale from 'antd/lib/date-picker/locale/pt_BR';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { reasonsToLeaveInvolutary } from '~/constants/people';
import {
  addSoluterExitInterviewInvoluntary,
  updateSoluterInvoluntaryExitInterview,
} from '~/store/fetchActions/fetchPeople';
import { clearSoluter, getSoluter } from '~/store/ducks/people';
import { SelectSearchPeople, SelectTowers } from '~/components';
import { setSelectedTower } from '~/store/ducks/towers';
import SoluterAllocationInfo from '../ExitInterviews/SoluterAllocationInfo';

const { TextArea } = Input;

export default function ExitInvolutaryInterviewForm({
  interview,
  isModalVisible,
  setModalVisible,
  customReload,
}) {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { isAddingExitInterview } = useSelector(state => state.loading);
  const { selectedTowerName, towers } = useSelector(state => state.towers);
  const { soluter } = useSelector(state => state.people);
  const [searchDismissedSoluters, setSearchDismissedSoluters] = useState(false);
  const [exSoluterSearchDismissed, setExSoluterSearchDismissed] = useState(
    false,
  );
  const [exSoluter, setExSoluter] = useState(null);
  const isFirstExSoluterSearchDismissedRender = useRef(true);

  useEffect(() => {
    if (interview && isModalVisible) {
      // confere se o motivo de saída está entre uma das opções pré-determinadas
      const isOtherReason = !Object.values(reasonsToLeaveInvolutary).includes(
        interview?.reason,
      );

      setExSoluter({ name: interview?.name, email: interview?.employeeEmail });
      form.setFieldsValue({
        person: {
          value: interview?.employeeEmail,
          label:
            interview?.type === 'INVOLUNTARY'
              ? interview?.leader
              : interview?.name,
        },
        name: interview?.name
          ? {
              value: interview?.employeeEmail,
              label: interview?.name,
              name: interview?.name,
              email: interview?.employeeEmail,
            }
          : null,
        leader: interview?.leader,
        project: interview?.project,
        type: interview?.type,
        reason: isOtherReason
          ? reasonsToLeaveInvolutary.OTHER
          : interview?.reason,
        otherReason: isOtherReason ? interview?.reason : '',
        leaderComment: interview?.leaderComment,
        wouldReturn: interview?.wouldReturn,
        otherComment: interview?.otherComment,
        costCenter: interview?.team,
        practice: interview?.practice,
        exitDate: interview?.exitDate
          ? moment(interview?.exitDate, 'DD-MM-YYYY')
          : null,
        interviewDate: interview?.interviewDate
          ? moment(interview?.interviewDate, 'DD-MM-YYYY')
          : null,
        BP: interview?.BP,
        BPComments: interview?.BPComments,
        isPcd: interview?.isPcd ?? false,
        pcdLeadershipDifficulty: interview?.pcdLeadershipDifficulty || '',
        pcdLeadershipSupport: interview?.pcdLeadershipSupport || '',
      });
    }
  }, [form, interview, isModalVisible]);

  const handlePersonChange = value => {
    dispatch(clearSoluter());
    setExSoluter(null);
    form.setFieldsValue({
      name: null,
      practice: undefined,
      costCenter: undefined,
      leader: value?.name || '',
    });
    if (value) {
      const { name, email } = value;
      if (name && email) {
        dispatch(
          getSoluter({
            name,
            email,
          }),
        );
      }
    }
  };

  const handleExSoluterChange = value => {
    if (value) {
      const { name, email } = value;
      setExSoluter({ name, email });
    } else {
      setExSoluter(null);
      form.setFieldsValue({
        practice: undefined,
        costCenter: undefined,
      });
    }
  };

  useEffect(() => {
    form.setFieldsValue({
      group: selectedTowerName,
    });
  }, [form, selectedTowerName]);

  useEffect(() => {
    if (isModalVisible && interview?.id && interview?.group && isModalVisible) {
      const selectedTower = towers.find(
        ({ name }) => name === interview?.group,
      );

      if (selectedTower) {
        const { _id, name } = selectedTower;
        dispatch(setSelectedTower({ value: _id, label: name }));
      }
    }
  }, [interview, towers, form, isModalVisible, dispatch]);

  useEffect(() => {
    if (!isModalVisible) {
      dispatch(setSelectedTower({ value: '', label: '' }));
      setExSoluter(null);
      form.setFieldsValue({
        group: '',
      });
    }
  }, [dispatch, isModalVisible, form]);

  useEffect(() => {
    if (isFirstExSoluterSearchDismissedRender.current) {
      isFirstExSoluterSearchDismissedRender.current = false;
      return;
    }
    form.setFieldsValue({
      name: null,
    });
    setExSoluter(null);
  }, [exSoluterSearchDismissed, form]);

  const handleSubmit = async values => {
    const formattedDate = values?.exitDate?.format('DD-MM-YYYY') || null;
    const formattedInterviewDate =
      values?.interviewDate?.format('DD-MM-YYYY') || null;
    const reason =
      values.reason === reasonsToLeaveInvolutary.OTHER
        ? values.otherReason
        : values.reason;

    const exSoluterName =
      values?.name?.name || values?.name?.label || exSoluter?.name || '';

    const payload = {
      BP: values?.BP || '',
      BPComments: values?.BPComments || '',
      exitDate: formattedDate,
      group: values?.group || '',
      interviewDate: formattedInterviewDate,
      leader: values?.leader || '',
      leaderComment: values?.leaderComment || '',
      name: exSoluterName,
      otherComments: values?.otherReason || '',
      project: values?.project || '',
      reason: reason || '',
      type: values?.type || '',
      wouldReturn: values?.wouldReturn === true,
      isPcd: values?.isPcd === true,
      pcdLeadershipDifficulty:
        values?.isPcd === true ? values?.pcdLeadershipDifficulty || '' : '',
      pcdLeadershipSupport:
        values?.isPcd === true ? values?.pcdLeadershipSupport || '' : '',
    };

    try {
      if (!interview) {
        await dispatch(
          addSoluterExitInterviewInvoluntary(exSoluter?.email, payload),
        );
      } else {
        await dispatch(
          updateSoluterInvoluntaryExitInterview(
            interview?.employeeEmail,
            interview?.id,
            payload,
          ),
        );
      }
      setModalVisible(false);
      form.resetFields();
    } catch (err) {
      console.log(err);
    } finally {
      customReload();
    }
  };

  const handleOk = () => {
    form
      .validateFields()
      .then(formValues => {
        handleSubmit(formValues);
      })
      .catch(console.error);
  };

  const validateMessages = {
    types: {
      email: 'Este não é um email válido',
    },
    string: {
      max: 'A resposta não deve ultrapassar ${max} caracteres.',
    },
    required: 'Este campo é obrigatório.',
  };

  const textAreaRules = {
    max: 500,
  };

  return (
    <Drawer
      visible={isModalVisible}
      onClose={() => setModalVisible(false)}
      title={
        interview
          ? `Editar entrevista de desligamento (${interview?.interviewDate})`
          : 'Adicionar entrevista de desligamento involutária'
      }
      width="75vw"
      footer={
        <Row justify="end">
          <Button onClick={() => setModalVisible(false)} className="mr-sm">
            Cancelar
          </Button>
          <Button
            type="primary"
            onClick={handleOk}
            loading={isAddingExitInterview}
          >
            Salvar
          </Button>
        </Row>
      }
      footerStyle={{
        position: 'relative',
      }}
    >
      <Form layout="vertical" form={form} validateMessages={validateMessages}>
        {!interview && (
          <>
            <p className="mb-xs mt-md">
              Buscar líder entrevistado na base de dados de:
            </p>
            <Radio.Group
              onChange={({ target }) => {
                setSearchDismissedSoluters(target.value);
              }}
              defaultValue={searchDismissedSoluters}
              style={{ 'margin-bottom': '10px' }}
            >
              <Space direction="horizontal">
                <Radio value={false} checked>
                  Líderes Ativos
                </Radio>
                <Radio data-testid="only-fired-checkbox" value>
                  Líderes Desligados
                </Radio>
              </Space>
            </Radio.Group>
          </>
        )}
        <Row gutter={16}>
          <Col>
            <Form.Item
              requiredMark
              rules={[
                {
                  required: true,
                },
              ]}
              name="person"
              label="Líder Entrevistado: "
              initialValue={
                interview
                  ? {
                      value: interview?.employeeEmail,
                      label: interview?.leader,
                    }
                  : null
              }
              className="mb-0"
            >
              <SelectSearchPeople
                disabled={interview}
                customStyle={{ minWidth: 300, marginBottom: 0 }}
                onChange={handlePersonChange}
                testId="select-soluter-interview"
                searchDismissed={searchDismissedSoluters}
                shouldReturnLabelInValue
                shouldReturnSoluterInfo
              />
              {/*  <Input name="leader" value={interview?.leader} /> */}
            </Form.Item>
          </Col>
          <Col>
            <Form.Item
              name="exitDate"
              label="Data do Desligamento"
              requiredMark
              rules={[
                {
                  required: true,
                },
              ]}
              initialValue={
                interview ? moment(interview?.exitDate, 'DD-MM-YYYY') : moment()
              }
            >
              <DatePicker
                locale={locale}
                format="DD/MM/YYYY"
                inputReadOnly
                defaultValue={
                  interview
                    ? moment(interview?.exitDate, 'DD-MM-YYYY')
                    : moment()
                }
              />
            </Form.Item>
          </Col>
          <Col>
            <Form.Item
              name="interviewDate"
              label="Data da Entrevista"
              requiredMark
              rules={[
                {
                  required: true,
                },
              ]}
              initialValue={
                interview
                  ? moment(interview?.interviewDate, 'DD-MM-YYYY')
                  : moment()
              }
            >
              <DatePicker
                locale={locale}
                format="DD/MM/YYYY"
                inputReadOnly
                defaultValue={
                  interview
                    ? moment(interview?.interviewDate, 'DD-MM-YYYY')
                    : moment()
                }
              />
            </Form.Item>
          </Col>
          <Col>
            <Form.Item
              requiredMark
              rules={[
                {
                  required: true,
                  max: 100,
                },
              ]}
              name="BP"
              label="Business Partner - Nome do entrevistador: "
              initialValue={
                interview
                  ? { value: interview?.BP, label: interview?.BP }
                  : null
              }
              className="mb-0"
            >
              <Input name="BP" value={interview?.BP} />
            </Form.Item>
          </Col>
          <Col>
            <Form.Item
              requiredMark
              name="group"
              label="Torre:"
              initialValue=""
              className="mb-0"
            >
              <SelectTowers
                selector={state => state.towers}
                dispatchDuck={setSelectedTower}
                fieldName="group"
                emptyOptionText="Nenhuma torre selecionada"
              />
            </Form.Item>
          </Col>
        </Row>
        <Divider />
        <Row justify="space-between" align="top" gutter={24}>
          <Col span={12}>
            <p className="mb-xs">Buscar ex-soluter na base de dados de:</p>
            <Radio.Group
              onChange={({ target }) => {
                setExSoluterSearchDismissed(target.value);
              }}
              defaultValue={exSoluterSearchDismissed}
              style={{ 'margin-bottom': '20px' }}
            >
              <Space direction="horizontal">
                <Radio value={false} checked>
                  Soluters Ativos
                </Radio>
                <Radio data-testid="only-fired-checkbox" value>
                  Soluters Desligados
                </Radio>
              </Space>
            </Radio.Group>
            <Form.Item
              label="1. Nome completo do Ex-Soluter:"
              name="name"
              requiredMark
              rules={[
                {
                  required: true,
                },
              ]}
              className="mb-0"
            >
              <SelectSearchPeople
                customStyle={{ minWidth: 300, marginBottom: 0 }}
                onChange={handleExSoluterChange}
                testId="select-ex-soluter-interview"
                shouldReturnLabelInValue
                shouldReturnSoluterInfo
                searchDismissed={exSoluterSearchDismissed}
              />
            </Form.Item>

            <SoluterAllocationInfo soluter={exSoluter || { email: null }} />
            <Form.Item name="practice" noStyle>
              <Input type="hidden" />
            </Form.Item>
            <Form.Item name="costCenter" noStyle>
              <Input type="hidden" />
            </Form.Item>
            <Form.Item
              label="2. Nome completo do Líder:"
              name="leader"
              initialValue={interview?.leader || soluter?.name}
              requiredMark
              rules={[
                {
                  required: true,
                  max: 100,
                },
              ]}
            >
              <Input name="name" value={interview?.name || soluter?.name} />
            </Form.Item>
            <Form.Item
              name="project"
              label="3. Projeto que atuava:"
              requiredMark
              rules={[{ max: 100, required: true }]}
            >
              <Input />
            </Form.Item>
            <Row>
              <Form.Item
                requiredMark
                rules={[{ required: true }]}
                name="type"
                label="4. Tipo do Desligamento"
              >
                <Radio.Group>
                  <Radio value="INVOLUNTARY" checked>
                    Involuntário (por solicitação da Solutis)
                  </Radio>
                </Radio.Group>
              </Form.Item>
            </Row>

            <Row align="middle" />
          </Col>
          <Col span={12}>
            {/* <Form.Item
              name="otherComment"
              label="4. Detalhamento do motivo do desligamento pelo líder:"
              requiredMark
              rules={[{ ...textAreaRules, required: true }]}
            >
              <TextArea />
            </Form.Item> */}
            <Form.Item
              label="5. Qual o principal motivo do seu desligamento?"
              name="reason"
              className="mb-0"
              requiredMark
              rules={[{ required: true, max: 500 }]}
            >
              <Radio.Group
                style={{
                  maxWidth: 500,
                  minWidth: 150,
                  display: 'grid',
                  gridTemplateColumns: 'auto',
                }}
              >
                <Radio value={reasonsToLeaveInvolutary.END_OF_PROJECT}>
                  {reasonsToLeaveInvolutary.END_OF_PROJECT}
                </Radio>
                <Radio
                  value={reasonsToLeaveInvolutary.LACK_OF_EFFORT}
                  className="exit-interview-checkbox"
                >
                  {reasonsToLeaveInvolutary.LACK_OF_EFFORT}
                </Radio>
                <Radio
                  value={reasonsToLeaveInvolutary.LACK_OF_COMMITMENT}
                  className="exit-interview-checkbox"
                >
                  {reasonsToLeaveInvolutary.LACK_OF_COMMITMENT}
                </Radio>
                <Radio
                  value={reasonsToLeaveInvolutary.DOESNT_FIT_THE_CULTURE}
                  className="exit-interview-checkbox"
                >
                  {reasonsToLeaveInvolutary.DOESNT_FIT_THE_CULTURE}
                </Radio>
                <Radio
                  value={reasonsToLeaveInvolutary.OTHER}
                  style={{ width: '100%' }}
                >
                  <Form.Item
                    name="otherReason"
                    className="mb-md"
                    rules={[{ max: 500 }]}
                  >
                    <Input
                      placeholder={reasonsToLeaveInvolutary.OTHER}
                      style={{ width: '25vw' }}
                    />
                  </Form.Item>
                </Radio>
              </Radio.Group>
            </Form.Item>
            <Form.Item
              name="wouldReturn"
              label="6. O líder recomenda o Soluter para retorno ?"
              requiredMark
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Radio.Group>
                <Radio value>Sim</Radio>
                <Radio value={false}>Não</Radio>
              </Radio.Group>
            </Form.Item>
            <Form.Item
              name="leaderComment"
              label="7. Alguma ressalva do líder?"
              requiredMark
              rules={[{ ...textAreaRules, required: true }]}
            >
              <TextArea />
            </Form.Item>
            <Form.Item
              name="BPComments"
              label="8. Comentários do Business Partner"
              rules={[{ ...textAreaRules, required: true }]}
            >
              <TextArea />
            </Form.Item>
            <Form.Item
              name="isPcd"
              label="9. O desligamento é de um Soluter PcD?"
              requiredMark
              initialValue={false}
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Radio.Group>
                <Radio value>Sim</Radio>
                <Radio value={false}>Não</Radio>
              </Radio.Group>
            </Form.Item>
            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) =>
                prevValues.isPcd !== currentValues.isPcd
              }
            >
              {() =>
                form.getFieldValue('isPcd') === true ? (
                  <>
                    <Form.Item
                      name="pcdLeadershipDifficulty"
                      label="10. Durante a gestão deste(a) colaborador(a), você encontrou alguma dificuldade relacionada à liderança de uma pessoa com deficiência?"
                      requiredMark
                      rules={[{ ...textAreaRules, required: true }]}
                    >
                      <TextArea />
                    </Form.Item>
                    <Form.Item
                      name="pcdLeadershipSupport"
                      label="11. Existe alguma ação que a Solutis poderia implementar para apoiar melhor as lideranças na gestão de pessoas com deficiência?"
                      requiredMark
                      rules={[{ ...textAreaRules, required: true }]}
                    >
                      <TextArea />
                    </Form.Item>
                  </>
                ) : null
              }
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Drawer>
  );
}

ExitInvolutaryInterviewForm.propTypes = {
  interview: PropTypes.object,
  isModalVisible: PropTypes.bool.isRequired,
  setModalVisible: PropTypes.func.isRequired,
  customReload: PropTypes.func,
};

ExitInvolutaryInterviewForm.defaultProps = {
  interview: null,
  customReload: () => {},
};
